import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import PropTypes from "prop-types";
import { ExternalLink, Loader2, PenLine, X } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { drawingApi } from "../../services/api";
import { apiErrorMessage } from "../../services/apiError";

/**
 * The task dialog's Drawings section (projects/drawings S3): preview tiles
 * of the linked drawings (each opens the drawing), a picker to link one
 * you can see, and unlink. Everyone sees only the linked drawings THEY can
 * see; the rest are counted, not named.
 */
function TaskDrawings({ taskId, onChange, refreshKey = 0 }) {
  const [links, setLinks] = useState([]);
  const [hidden, setHidden] = useState(0);
  const [canRemoveHidden, setCanRemoveHidden] = useState(false);
  const [total, setTotal] = useState(0); // drawings you can see, all pages
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [picking, setPicking] = useState(false);
  const [query, setQuery] = useState("");
  const [search, setSearch] = useState("");
  const [choices, setChoices] = useState([]);
  const [choicesLoading, setChoicesLoading] = useState(false);
  const [busy, setBusy] = useState(null); // the drawing id being (un)linked
  // Two unlinks in a row re-list twice; only the newest answer may land
  // (an older one would bring back a tile that's already gone).
  const loadGeneration = useRef(0);

  const load = useCallback(async () => {
    const generation = ++loadGeneration.current;
    try {
      const { data } = await drawingApi.taskLinks(taskId);
      if (generation !== loadGeneration.current) return;
      setLinks(data.links || []);
      setHidden(data.hidden || 0);
      setCanRemoveHidden(Boolean(data.can_remove_hidden));
      setError(null);
    } catch (err) {
      if (generation !== loadGeneration.current) return;
      setError(apiErrorMessage(err, "Couldn't load the linked drawings."));
    } finally {
      if (generation === loadGeneration.current) setLoading(false);
    }
  }, [taskId]);

  useEffect(() => {
    load();
  }, [load]);

  // someone else linked or unlinked a drawing (the live thread saw it)
  useEffect(() => {
    if (refreshKey) load();
  }, [refreshKey, load]);

  useEffect(() => {
    if (!picking) return undefined;
    let cancelled = false;
    setChoicesLoading(true);
    drawingApi
      .getAll(search ? { search } : {})
      .then(({ data }) => {
        if (cancelled) return;
        setChoices(data.results || []);
        setTotal(data.count || 0);
      })
      .catch((err) => {
        if (!cancelled)
          setError(apiErrorMessage(err, "Couldn't load your drawings."));
      })
      .finally(() => {
        if (!cancelled) setChoicesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [picking, search]);

  const linkedIds = new Set(links.map((l) => l.drawing.id));
  const available = choices.filter((d) => !linkedIds.has(d.id));

  const link = async (drawing) => {
    setBusy(drawing.id);
    setError(null);
    try {
      await drawingApi.link(taskId, drawing.id);
      await load();
      setPicking(false);
      setQuery("");
      setSearch("");
      onChange?.();
    } catch (err) {
      setError(apiErrorMessage(err, "Couldn't link that drawing."));
    } finally {
      setBusy(null);
    }
  };

  const removeHidden = async () => {
    setBusy("hidden");
    setError(null);
    try {
      await drawingApi.removeHidden(taskId);
      await load();
      onChange?.();
    } catch (err) {
      setError(apiErrorMessage(err, "Couldn't remove them."));
    } finally {
      setBusy(null);
    }
  };

  const unlink = async (drawing) => {
    setBusy(drawing.id);
    setError(null);
    try {
      await drawingApi.unlink(taskId, drawing.id);
      await load();
      onChange?.();
    } catch (err) {
      setError(apiErrorMessage(err, "Couldn't unlink that drawing."));
    } finally {
      setBusy(null);
    }
  };

  return (
    <section aria-label="Drawings" className="mt-4 border-t pt-4">
      <h3 className="mb-2 text-xs font-semibold tracking-wide text-muted-foreground uppercase">
        Drawings{links.length > 0 && ` (${links.length})`}
      </h3>

      {loading && (
        <p className="text-sm text-muted-foreground" role="status">
          Loading drawings…
        </p>
      )}

      {!loading && links.length > 0 && (
        <ul className="mb-2 grid list-none grid-cols-2 gap-2 p-0 sm:grid-cols-3">
          {links.map(({ drawing }) => (
            <li
              key={drawing.id}
              className="relative overflow-hidden rounded-md border bg-card"
            >
              {/* A new tab: leaving would close this dialog under an
                  upload or unsaved edits. */}
              <Link
                to={`/drawings/${drawing.id}`}
                target="_blank"
                rel="noreferrer"
                className="block"
                title="Opens in a new tab"
              >
                <div className="flex aspect-[4/3] items-center justify-center bg-white">
                  {drawing.preview_url ? (
                    <img
                      src={drawing.preview_url}
                      alt=""
                      loading="lazy"
                      className="size-full object-contain"
                    />
                  ) : (
                    <span className="text-xs text-neutral-500">
                      No preview yet
                    </span>
                  )}
                </div>
                <span className="flex items-center gap-1 px-2 py-1 text-sm font-medium">
                  <span className="truncate">{drawing.title}</span>
                  <ExternalLink
                    className="size-3 shrink-0 text-muted-foreground"
                    aria-hidden="true"
                  />
                  <span className="sr-only">(opens in a new tab)</span>
                </span>
              </Link>
              <Button
                type="button"
                variant="secondary"
                size="icon-xs"
                className="absolute top-1 right-1"
                aria-label={`Unlink ${drawing.title}`}
                title="Unlink from this task (the drawing itself stays)"
                disabled={busy === drawing.id}
                onClick={() => unlink(drawing)}
              >
                <X aria-hidden="true" />
              </Button>
            </li>
          ))}
        </ul>
      )}

      {!loading && hidden > 0 && (
        <p className="mb-2 flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
          <span>
            {hidden === 1
              ? "1 more linked drawing is private to someone else."
              : `${hidden} more linked drawings are private to someone else.`}
          </span>
          {canRemoveHidden && (
            <Button
              type="button"
              variant="link"
              size="xs"
              className="h-auto p-0"
              disabled={busy !== null}
              onClick={removeHidden}
              title="Unlink the drawings you can't see (they stay with their owners)"
            >
              {hidden === 1 ? "Remove it" : "Remove them"}
            </Button>
          )}
        </p>
      )}

      {!loading && links.length === 0 && hidden === 0 && !picking && (
        <p className="mb-2 text-sm text-muted-foreground">
          No drawings linked. Link a sketch or diagram to keep it with the task.
        </p>
      )}

      {error && (
        <p className="mb-2 text-sm text-destructive" role="alert">
          {error}
        </p>
      )}

      {!picking ? (
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => setPicking(true)}
        >
          <PenLine className="size-4" aria-hidden="true" />
          Link a drawing
        </Button>
      ) : (
        <div className="rounded-md border p-2">
          <div className="mb-2 flex gap-2">
            <Input
              type="search"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              onKeyDown={(event) => {
                // Inside the task form: Enter searches, it doesn't save.
                if (event.key === "Enter") {
                  event.preventDefault();
                  setSearch(query.trim());
                }
              }}
              placeholder="Search your drawings…"
              aria-label="Find a drawing to link"
              className="h-8"
            />
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setSearch(query.trim())}
            >
              Search
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => {
                setPicking(false);
                setQuery("");
                setSearch("");
              }}
            >
              Cancel
            </Button>
          </div>
          {choicesLoading ? (
            <p className="text-sm text-muted-foreground" role="status">
              <Loader2 className="mr-1 inline size-4 animate-spin" />
              Finding drawings…
            </p>
          ) : available.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              {choices.length > 0
                ? total > choices.length
                  ? "These are all linked already — search for others."
                  : "Every drawing you can see is linked already."
                : search
                  ? "No drawings match that search."
                  : "No drawings to link yet — make one under Drawings."}
            </p>
          ) : (
            <ul className="flex max-h-48 list-none flex-col gap-1 overflow-y-auto p-0">
              {available.map((drawing) => (
                <li key={drawing.id}>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="w-full justify-between"
                    disabled={busy !== null}
                    onClick={() => link(drawing)}
                    aria-label={`Link ${drawing.title}`}
                  >
                    <span className="truncate">{drawing.title}</span>
                    <Badge variant="secondary" className="font-normal">
                      {drawing.visibility === "household"
                        ? "Household"
                        : "Private"}
                    </Badge>
                  </Button>
                </li>
              ))}
            </ul>
          )}
          {!choicesLoading &&
            total > choices.length &&
            available.length > 0 && (
              <p className="mt-1 text-xs text-muted-foreground">
                Showing the {choices.length} most recent of {total} — search to
                find others.
              </p>
            )}
        </div>
      )}
    </section>
  );
}

TaskDrawings.propTypes = {
  taskId: PropTypes.number.isRequired,
  onChange: PropTypes.func,
  // bumped when someone else changes this task's drawing links
  refreshKey: PropTypes.number,
};

export default TaskDrawings;
